import React, { useEffect, useState } from "react"
import axios from "axios"
import { useSelector, connect } from "react-redux"
import { useNavigate } from "react-router-dom"
import {
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Spinner,
  Table,
} from "reactstrap"
import config from "config/config"
import PrimaryButton from "newcomponents/primary-button"
import { setBreadcrumbItems } from "../../store/actions"

const statusColors = {
  draft: "secondary",
  active: "success",
  paused: "warning",
  completed: "info",
  deleted: "danger",
}

const SocialCampaignOverview = props => {
  document.title = "Social Media Campaigns | AeraVue"

  const navigate = useNavigate()
  const activeOrganization = useSelector(
    state => state.organizationReducer.activeOrganization,
  )
  const userData = useSelector(state => state.Login.userData)

  const [campaigns, setCampaigns] = useState([])
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)

  const breadcrumbItems = [
    { title: `${activeOrganization.name}`, link: "#" },
    { title: "Social Media Campaigns", link: "#" },
  ]

  useEffect(() => {
    props.setBreadcrumbItems("Social Media Campaigns", breadcrumbItems)
  }, [])

  const fetchCampaigns = async () => {
    setLoading(true)
    setErr(null)
    try {
      const response = await axios.get(
        `${config.backendUrl}/social-media-campaigns`,
        {
          params: { organizationId: activeOrganization.id },
          headers: { Authorization: `Bearer ${userData.token}` },
        },
      )
      setCampaigns(response.data.campaigns || [])
    } catch (error) {
      setErr(
        error.response?.data?.message || "Failed to fetch social media campaigns.",
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (activeOrganization?.id) fetchCampaigns()
  }, [activeOrganization?.id])

  return (
    <React.Fragment>
      <Row className="align-items-center mb-3">
        <Col>
          <h4 className="mb-0">Social Media Campaigns</h4>
        </Col>
        <Col className="text-end">
          <PrimaryButton
            style={{ padding: "10px 30px", borderRadius: "8px" }}
            onClick={() => navigate("/dashboard/social-campaigns/create")}
          >
            Create New +
          </PrimaryButton>
        </Col>
      </Row>

      <Card>
        <CardBody>
          {loading && (
            <div className="text-center py-4">
              <Spinner color="primary" />
            </div>
          )}

          {!loading && err && <p className="text-danger">{err}</p>}

          {!loading && !err && campaigns.length === 0 && (
            <p className="text-muted mb-0">
              No social media campaigns yet. Create one to have AI generate
              posts for Facebook and Instagram.
            </p>
          )}

          {!loading && !err && campaigns.length > 0 && (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Platforms</th>
                  <th>Date Range</th>
                  <th>Status</th>
                  <th>Posts</th>
                  <th>Pending Review</th>
                  <th>Published</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map(campaign => (
                  <tr
                    key={campaign.id}
                    style={{ cursor: "pointer" }}
                    onClick={() =>
                      navigate(`/dashboard/social-campaigns/${campaign.id}`)
                    }
                  >
                    <td>{campaign.campaignTitle}</td>
                    <td>{(campaign.platforms || []).join(", ")}</td>
                    <td>
                      {campaign.startDate?.slice(0, 10)} -{" "}
                      {campaign.endDate?.slice(0, 10)}
                    </td>
                    <td>
                      <Badge color={statusColors[campaign.status] || "secondary"}>
                        {campaign.status}
                      </Badge>
                    </td>
                    <td>{campaign.postCount}</td>
                    <td>{campaign.pendingReviewCount}</td>
                    <td>{campaign.publishedCount}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>
    </React.Fragment>
  )
}

export default connect(null, { setBreadcrumbItems })(SocialCampaignOverview)
